/**
 * A loja de funcionalidades (#560): o que a organização pode adicionar, num
 * catálogo só.
 *
 * ── Por que um modelo à parte da tela ─────────────────────────────────────
 *
 * Os itens vêm de três leituras que não se conhecem — as funcionalidades
 * (`/v1/admin/flags`), o catálogo de portais e os canais conectados —, e cada
 * uma tem a sua regra de estado. Juntar as três aqui, numa função pura, é o
 * que deixa a regra ser testada sem montar a tela, e deixa a tela só desenhar.
 *
 * ── O que NÃO entra ───────────────────────────────────────────────────────
 *
 * Preço e contratação. A issue deixa em aberto se item pago entra na loja, e
 * regra comercial não se decide no código (§21): por ora, só entra o que já
 * está incluído no plano.
 */

import type { FlagDoTenant, Plataforma, TipoDeCanal } from '@cliqcrm/contracts';
import type { NomeDoIcone } from '@/shared/ui';
import { CATALOGO_DE_CANAIS, disponibilidadeDoCanal, EM_BREVE } from '../ui/canais';

export type CategoriaDaLoja = 'canais' | 'portais' | 'crm' | 'ia';

/** Na ordem em que aparecem nos filtros e na vitrine. */
export const CATEGORIAS_DA_LOJA: readonly { chave: CategoriaDaLoja; nome: string }[] = [
  { chave: 'canais', nome: 'Canais de atendimento' },
  { chave: 'portais', nome: 'Portais e anúncios' },
  { chave: 'crm', nome: 'Recursos do CRM' },
  { chave: 'ia', nome: 'IA' },
];

export type EstadoNaLoja = 'adicionada' | 'disponivel' | 'em-breve' | 'indisponivel';

export const ROTULO_DO_ESTADO: Readonly<Record<EstadoNaLoja, string>> = {
  adicionada: 'Adicionada',
  disponivel: 'Disponível',
  'em-breve': 'Em breve',
  indisponivel: 'Indisponível',
};

/**
 * O que "Adicionar" faz.
 *
 * Canal tem fluxo próprio — conta na Meta, código no site, quem atende —, e
 * esse fluxo já existe em Conexões: a loja leva até ele, em vez de ter um
 * segundo jeito de conectar. Funcionalidade e portal são uma chave por
 * organização, e ligar é homologar (princípio 08).
 */
export type AcaoDaLoja =
  { tipo: 'canal'; canal: TipoDeCanal } | { tipo: 'flag'; chave: string; permanente: boolean };

export type IconeDoItem = { canal: TipoDeCanal } | { nome: NomeDoIcone };

export interface ItemDaLoja {
  /** Único na loja inteira: `canal:whatsapp`, `flag:push_com_conteudo`. */
  readonly id: string;
  readonly categoria: CategoriaDaLoja;
  readonly nome: string;
  /** Uma linha, para o cartão. */
  readonly linha: string;
  /** O que faz, por extenso, para o detalhe. */
  readonly sobre: string;
  /** O que precisa para funcionar. Vazio quando não precisa de nada. */
  readonly requisitos: readonly string[];
  readonly estado: EstadoNaLoja;
  /**
   * Por que não se adiciona por aqui, quando não se adiciona.
   *
   * Num canal JÁ adicionado ele continua aqui: um TikTok conectado direto no
   * motor está "Adicionado", e ainda assim não se adiciona OUTRO pela loja — o
   * assistente de Conexões não tem o passo dele.
   */
  readonly motivo?: string;
  readonly icone: IconeDoItem;
  readonly acao: AcaoDaLoja;
  /**
   * A chave técnica, no detalhe, para o suporte — nunca em destaque. É o nome
   * como a API o conhece: a chave da funcionalidade ou do portal, o tipo do
   * canal.
   */
  readonly referencia: string;
}

// ── Canais ──────────────────────────────────────────────────────────────────

/** O que cada canal pede de quem vai conectá-lo. */
const REQUISITOS_DO_CANAL: Partial<Record<TipoDeCanal, readonly string[]>> = {
  whatsapp: ['Uma conta do WhatsApp Business na Meta.', 'O número que vai atender.'],
  instagram: [
    'Uma conta profissional do Instagram.',
    'A conta ligada a uma página do Facebook que você administra.',
  ],
  facebook: ['Uma página do Facebook que você administra.'],
  web_chat: ['Acesso ao site da empresa para colar um trecho de código.'],
  api: ['Um sistema da empresa que envie e receba as mensagens por API.'],
  tiktok: ['Uma conta comercial do TikTok.'],
};

function itensDeCanais(
  conectados: readonly { tipo: TipoDeCanal }[],
  whatsappConfigurado: boolean,
): ItemDaLoja[] {
  const tiposConectados = new Set(conectados.map((c) => c.tipo));

  return CATALOGO_DE_CANAIS.map((canal) => {
    const motivo = disponibilidadeDoCanal(canal.tipo, whatsappConfigurado);
    const estado: EstadoNaLoja = tiposConectados.has(canal.tipo)
      ? 'adicionada'
      : motivo === null
        ? 'disponivel'
        : motivo === EM_BREVE
          ? 'em-breve'
          : 'indisponivel';

    return {
      id: `canal:${canal.tipo}`,
      categoria: 'canais',
      nome: canal.nome,
      linha: canal.descricao,
      sobre: `${canal.descricao} As conversas que chegam por ele entram no Atendimento, junto com as dos outros canais.`,
      requisitos: REQUISITOS_DO_CANAL[canal.tipo] ?? [],
      estado,
      ...(motivo !== null ? { motivo } : {}),
      icone: { canal: canal.tipo },
      acao: { tipo: 'canal', canal: canal.tipo },
      referencia: canal.tipo,
    };
  });
}

// ── Portais ─────────────────────────────────────────────────────────────────

/**
 * Os portais que só fazem sentido num segmento.
 *
 * A organização ainda não tem segmento no cadastro (decisão de 01/10/2026,
 * #560): até ter, o portal de um segmento só aparece na loja para quem já o
 * tem ligado. Os gerais aparecem para todos. Quando o segmento existir, esta
 * lista vira um campo do catálogo e o filtro passa a ser por ele.
 */
const PORTAIS_DE_SEGMENTO: ReadonlySet<string> = new Set(['usadosbr']);

/**
 * "do portal", e não "do OLX Chat": o nome vem do catálogo, e o artigo certo
 * ("da OLX", "do RD Station") não vem junto. O cartão já mostra o nome.
 */
function linhaDoPortal(portal: Plataforma): string {
  const leads = portal.capacidades.includes('lead');
  const conversas = portal.capacidades.includes('chat') || portal.capacidades.includes('mensagem');
  const oQue =
    leads && conversas
      ? 'os interessados e as conversas'
      : conversas
        ? 'as conversas'
        : 'os interessados';
  return `Traz para o CRM ${oQue} do portal.`;
}

function itensDePortais(plataformas: readonly Plataforma[]): ItemDaLoja[] {
  return plataformas
    .filter((p) => p.ativa_no_tenant || !PORTAIS_DE_SEGMENTO.has(p.provider))
    .map((portal) => {
      /*
       * `fichado` é saber o que o portal exige sem ter código que fale com
       * ele. Na loja isso é "Em breve" — oferecer "Adicionar" ligaria uma chave
       * que não faz nada, e a organização esperaria contatos que nunca chegam.
       */
      const estado: EstadoNaLoja = portal.ativa_no_tenant
        ? 'adicionada'
        : portal.situacao === 'fichado'
          ? 'em-breve'
          : 'disponivel';

      // Só o que a ORGANIZAÇÃO fornece: o escopo `aplicacao` é credencial
      // da Cliqfy, e listá-lo aqui pediria à pessoa algo que ela não tem.
      const daOrganizacao = portal.configuracao
        .filter((c) => c.escopo === 'revenda' && c.obrigatorio)
        .map((c) => c.rotulo);

      const linha = linhaDoPortal(portal);
      return {
        id: `portal:${portal.provider}`,
        categoria: 'portais',
        nome: portal.rotulo,
        linha,
        sobre: `${linha} Cada contato vira um cliente com a origem marcada, e entra no funil como os que chegam pelos canais.`,
        requisitos: ['Uma conta da organização no portal.', ...daOrganizacao],
        estado,
        ...(estado === 'em-breve'
          ? { motivo: 'A ligação com este portal ainda está sendo construída.' }
          : {}),
        icone: { nome: 'portal' },
        acao: { tipo: 'flag', chave: portal.feature_flag, permanente: false },
        referencia: portal.feature_flag,
      };
    });
}

// ── Funcionalidades ─────────────────────────────────────────────────────────

/**
 * Como cada funcionalidade se apresenta na loja.
 *
 * A `descricao` do catálogo é escrita para quem mantém o produto, e às vezes
 * carrega código de task — "(D08 prepara, F04 implementa)". Aqui fica o texto
 * de quem decide adicionar. A funcionalidade que não está nesta lista aparece
 * mesmo assim, com a descrição do catálogo: uma flag nova nunca some da loja
 * por falta de texto.
 */
const APRESENTACAO_DA_FLAG: Readonly<
  Record<
    string,
    {
      nome: string;
      categoria: CategoriaDaLoja;
      linha: string;
      sobre?: string;
      requisitos?: readonly string[];
      icone: NomeDoIcone;
    }
  >
> = {
  resumo_de_ia_ao_encerrar: {
    nome: 'Resumo da conversa',
    categoria: 'ia',
    linha: 'Ao encerrar uma conversa, a IA escreve um resumo e o salva no cliente.',
    sobre:
      'Quando a conversa é encerrada, a IA lê o que foi dito e escreve um resumo curto, salvo como nota no cliente. A nota diz que veio da IA, e quem atende pode revisá-la.',
    icone: 'ia',
  },
  clinica: {
    nome: 'Clínica',
    categoria: 'crm',
    linha: 'A ficha do paciente no cadastro de cada cliente.',
    sobre:
      'Cada cliente ganha uma ficha de paciente: nome social, nascimento, endereço, profissão, estado civil, contato de emergência e a melhor forma e horário de contato. A ficha fica no cadastro do cliente, e quem atende vê tudo no mesmo lugar.',
    icone: 'pessoa',
  },
  automacoes: {
    nome: 'Automações',
    categoria: 'crm',
    linha: 'Regras que agem sozinhas quando algo acontece no CRM.',
    sobre:
      'Libera a área de Automações para gestores e administradores: regras que movem oportunidades, distribuem contatos, criam tarefas e enviam mensagens quando algo acontece. A criação das regras chega a esta área aos poucos.',
    icone: 'automacao',
  },
  push_com_conteudo: {
    nome: 'Aviso com a mensagem',
    categoria: 'crm',
    linha: 'O aviso de mensagem nova mostra quem escreveu e o começo do texto.',
    requisitos: ['Os avisos do navegador ligados no computador de cada pessoa.'],
    icone: 'sino',
  },
};

/** `resumo_de_ia_ao_encerrar` → `Resumo de ia ao encerrar`. Só para a flag sem texto. */
function nomeDaChave(chave: string): string {
  const texto = chave.replace(/_/g, ' ');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function itensDeFlags(
  flags: readonly FlagDoTenant[],
  chavesDePortal: ReadonlySet<string>,
): ItemDaLoja[] {
  return (
    flags
      /*
       * A chave de portal já aparece como portal. O prefixo cobre quem não lê
       * o catálogo de portais (a matriz o dá só a quem vê integração): para
       * essa pessoa, a chave de um conector não é recurso do CRM.
       */
      .filter((f) => !chavesDePortal.has(f.chave) && !f.chave.startsWith('conector_'))
      .map((flag) => {
        const apresentacao = APRESENTACAO_DA_FLAG[flag.chave];
        /*
         * `permanente` não tem botão, ligada ou não — a regra da tela de
         * antes, mantida. Ligada, "não pode ser removida"; desligada, ela não
         * se adiciona por aqui, e a loja diz isso em vez de oferecer.
         */
        const estado: EstadoNaLoja = flag.ligada
          ? 'adicionada'
          : flag.permanente
            ? 'indisponivel'
            : 'disponivel';

        return {
          id: `flag:${flag.chave}`,
          categoria: apresentacao?.categoria ?? 'crm',
          nome: apresentacao?.nome ?? nomeDaChave(flag.chave),
          linha: apresentacao?.linha ?? flag.descricao,
          sobre: apresentacao?.sobre ?? flag.descricao,
          requisitos: apresentacao?.requisitos ?? [],
          estado,
          ...(estado === 'indisponivel'
            ? { motivo: 'Esta funcionalidade ainda não se adiciona por aqui.' }
            : {}),
          icone: { nome: apresentacao?.icone ?? 'configuracoes' },
          acao: { tipo: 'flag', chave: flag.chave, permanente: flag.permanente },
          referencia: flag.chave,
        };
      })
  );
}

// ── A loja ──────────────────────────────────────────────────────────────────

export interface FontesDaLoja {
  readonly flags: readonly FlagDoTenant[];
  /**
   * `null` quando a pessoa não vê integração: canais e portais ficam de fora
   * da loja dela, pela mesma razão de Conexões sumir do menu.
   */
  readonly integracao: {
    /**
     * `null` enquanto a leitura não chegou, ou se falhou: a categoria fica de
     * fora em vez de dizer "Disponível" para um canal que talvez já esteja
     * conectado — ou "Em breve" para um portal que a organização já tem.
     */
    readonly plataformas: readonly Plataforma[] | null;
    readonly canaisConectados: readonly { tipo: TipoDeCanal }[] | null;
    readonly whatsappConfigurado: boolean;
  } | null;
}

const ORDEM_DO_ESTADO: Readonly<Record<EstadoNaLoja, number>> = {
  adicionada: 0,
  disponivel: 1,
  'em-breve': 2,
  indisponivel: 3,
};

const ORDEM_DA_CATEGORIA: Readonly<Record<CategoriaDaLoja, number>> = {
  canais: 0,
  portais: 1,
  crm: 2,
  ia: 3,
};

/**
 * Todos os itens, por categoria e, dentro dela, o que a organização tem
 * primeiro, depois o que pode adicionar, e por último o que ainda não pode.
 *
 * A ordem dos canais dentro do mesmo estado é a do catálogo (do mais usado ao
 * menos) — `sort` é estável.
 */
export function montarLoja(fontes: FontesDaLoja): ItemDaLoja[] {
  const { integracao } = fontes;
  const chavesDePortal = new Set(integracao?.plataformas?.map((p) => p.feature_flag) ?? []);

  const itens = [
    ...(integracao?.canaisConectados
      ? itensDeCanais(integracao.canaisConectados, integracao.whatsappConfigurado)
      : []),
    ...(integracao?.plataformas ? itensDePortais(integracao.plataformas) : []),
    ...itensDeFlags(fontes.flags, chavesDePortal),
  ];

  return itens.sort(
    (a, b) =>
      ORDEM_DA_CATEGORIA[a.categoria] - ORDEM_DA_CATEGORIA[b.categoria] ||
      ORDEM_DO_ESTADO[a.estado] - ORDEM_DO_ESTADO[b.estado],
  );
}

/** Sem acento e em minúsculas: "transcricao" acha "Transcrição". */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * O recorte da vitrine: a categoria escolhida e a busca.
 *
 * A busca olha o nome, a linha, a categoria e a referência técnica — esta
 * última porque é o que o suporte dita ao telefone ("procura por
 * `resumo_de_ia`").
 */
export function filtrarLoja(
  itens: readonly ItemDaLoja[],
  categoria: CategoriaDaLoja | null,
  busca: string,
): ItemDaLoja[] {
  const termos = normalizar(busca).split(/\s+/).filter(Boolean);
  const nomeDaCategoria = new Map(CATEGORIAS_DA_LOJA.map((c) => [c.chave, c.nome]));

  return itens.filter((item) => {
    if (categoria !== null && item.categoria !== categoria) return false;
    if (termos.length === 0) return true;
    const texto = normalizar(
      [item.nome, item.linha, nomeDaCategoria.get(item.categoria) ?? '', item.referencia].join(' '),
    );
    return termos.every((termo) => texto.includes(termo));
  });
}
